import {capitalize} from 'lodash-es';

import type {ProcessorSettingTarget} from '@/screens/Processor/Corridors/types';
import {
  AfriexPaymentProcessors,
  AfriexProcessorPaymentTypes,
} from '@/screens/Processor/Transactions/types';
import type {AfriexOTCRate} from '@/types';
import {
  AfriexCurrencyCode,
  AfriexReferralStatus,
  AfriexRewardTier,
  AfriexRewardType,
  AfriexRoles,
  AfriexTierStatus,
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  AfriexTransactionType,
  BUSINESS_RESTRICTED_PROCESSORS,
} from '@/types';
import {
  AfriexBlockReason,
  AfriexUnblockReason,
} from '@/types/AfriexBlockReason';
import {BusinessTransactionChannel} from '@/types/AfriexBusinessTransactionChannel';
import {BusinessTransactionStatus} from '@/types/AfriexBusinessTransactionStatus';
import {BusinessTransactionType} from '@/types/AfriexBusinessTransactionType';
import {AfriexDisputeStatus} from '@/types/AfriexDispute';
import {AfriexTransactionStatus, LedgerReasons} from '@/types/Transaction';
import {capitalizeWords} from '@/utils/dashboard';

export const doNothingAsync = () => Promise.resolve();
export const doNothing = () => null;

export const getCountryOptions = (countryOptions: Record<string, string>) =>
  Object.keys(countryOptions).map((key) => {
    const displayKey = countryOptions[key];
    return {
      key: displayKey?.replace(/undefined/g, '').trim(),
      value: key,
    };
  });

export const getCurrencyOptions = (currencyOptions: Record<string, string>) =>
  Object.keys(currencyOptions)
    .filter((currency) => currency in AfriexCurrencyCode) // Only include supported currencies
    .map((currency) => {
      const displayKey = currencyOptions[currency];
      return {
        key: displayKey?.replace(/undefined/g, '').trim(),
        value: currency,
      };
    });

export const getTransactionChannelsOptions = (
  channelsToExclude?: AfriexTransactionChannel[],
) =>
  Object.entries(AfriexTransactionChannel)
    .filter(([_, value]) => !channelsToExclude?.includes(value))
    .map(([key, value]) => ({
      key: capitalizeWords(key?.replace(/_/g, ' ')),
      value,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

export const getBusinessTransactionChannelsOptions = () =>
  Object.entries(BusinessTransactionChannel)
    .map(([key, value]) => ({
      key: capitalizeWords(key?.replace(/_/g, ' ')),
      value,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

/**
 * The processors the capability matrix reports for a currency, per channel.
 *
 * The server computes one target-agnostic matrix (it omits userType so a single
 * response can serve all three tabs), so retail-only rails appear in it. Pass
 * the `target` being rendered and they are dropped for `business` and `api`,
 * matching what the PATCH schema will accept.
 */
export const getChannelProcessors = (
  capability:
    | Partial<
        Record<
          AfriexTransactionChannel,
          Partial<Record<AfriexCurrencyCode, string>>
        >
      >
    | undefined,
  currencyCode: AfriexCurrencyCode,
  target: ProcessorSettingTarget = 'user',
) => {
  const isSettable = (name: string) =>
    target === 'user' ||
    !BUSINESS_RESTRICTED_PROCESSORS.includes(
      name as AfriexTransactionProcessor,
    );
  return Object.entries(capability ?? {})
    .map(([ch, currencyMap]) => ({
      channel: ch as AfriexTransactionChannel,
      processors: new Set(
        (currencyMap?.[currencyCode] ?? '')
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean)
          .filter(isSettable),
      ),
    }))
    .filter(({processors}) => processors.size > 0);
};

export const getTransactionProcessorsOptions = (
  processorsToExclude?: AfriexTransactionProcessor[],
) =>
  Object.entries(AfriexTransactionProcessor)
    .filter(([_, value]) => !processorsToExclude?.includes(value))
    .map(([key, value]) => ({
      key: capitalizeWords(key?.replace(/_/g, ' ')),
      value,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

// Builds processor dropdown options from a list of processor codes fetched
// from the server (GET /v2/admin/processors) so the admin stays in sync with
// the backend's processor enum instead of relying on the local enum, which
// drifts. UNKNOWN is filtered out so it can never be selected (matching the
// local enum, which keeps UNKNOWN commented out).
export const getProcessorOptionsFromCodes = (
  codes: string[],
  processorsToExclude?: AfriexTransactionProcessor[],
) =>
  codes
    .filter(
      (code) =>
        code !== 'UNKNOWN' &&
        !processorsToExclude?.includes(code as AfriexTransactionProcessor),
    )
    .map((code) => ({
      key: capitalizeWords(code?.replace(/_/g, ' ')),
      value: code,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

export const getProcessorTxOptions = () =>
  Object.entries(AfriexPaymentProcessors)
    .map(([key, value]) => ({
      key: capitalizeWords(key?.replace(/_/g, ' ')),
      value,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

export const getProcessorTxTypeOptions = (
  typesToExclude?: AfriexProcessorPaymentTypes[],
) =>
  Object.entries(AfriexProcessorPaymentTypes)
    .filter(([_, value]) => !typesToExclude?.includes(value))
    .map(([key, value]) => ({
      key: capitalizeWords(key?.replace(/_/g, ' ')),
      value,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

export const getTransactionTypesOptions = () =>
  Object.entries(AfriexTransactionType).map(([key, value]) => ({
    key: capitalizeWords(key?.replace(/_/g, ' ')),
    value,
  }));

export const getTransactionStatusOptions = () =>
  Object.entries(AfriexTransactionStatus).map(([key, value]) => ({
    key: capitalizeWords(key?.replaceAll(/_/g, ' ')),
    value,
  }));

export const getBusinessTransactionTypesOptions = () =>
  Object.entries(BusinessTransactionType).map(([key, value]) => ({
    key: capitalizeWords(key?.replace(/_/g, ' ')),
    value,
  }));

export const getBusinessTransactionStatusOptions = () =>
  Object.entries(BusinessTransactionStatus).map(([key, value]) => ({
    key: capitalizeWords(key?.replaceAll(/_/g, ' ')),
    value,
  }));

export const getAdminRoleOptions = (excludedRoles = [] as AfriexRoles[]) => {
  return Object.entries(AfriexRoles)
    .filter(([_, value]) => !excludedRoles.includes(value)) // Exclude the 'USER' and 'ENGINEERING_MANAGER role
    .map(([key, value]) => ({
      key: capitalizeWords(key.replace(/_/g, ' ')), // Format key nicely
      value,
    }));
};

export const getReferralStatusOptions = () =>
  Object.keys(AfriexReferralStatus).map((key) => ({
    key: capitalize(key),
    value: AfriexReferralStatus[key as keyof typeof AfriexReferralStatus],
  }));

export const getDisputeStatusOptions = () =>
  Object.keys(AfriexDisputeStatus).map((key) => ({
    key: capitalize(key),
    value: AfriexDisputeStatus[key as keyof typeof AfriexDisputeStatus],
  }));

// The reward-type enum used to spell keys with a space (e.g. "Gift Card") so
// they read as-is in the dropdown; core's canonical shape is PascalCase
// ("GiftCard"). Insert the missing spaces back for the display label.
const humanizePascal = (s: string) => s.replace(/([a-z])([A-Z])/g, '$1 $2');

export const getRewardTypeOptions = () =>
  Object.keys(AfriexRewardType).map((key) => ({
    key: humanizePascal(key),
    value: AfriexRewardType[key as keyof typeof AfriexRewardType],
  }));

export const getRewardTierOptions = () =>
  Object.keys(AfriexRewardTier).map((key) => ({
    key: key,
    value: AfriexRewardTier[key as keyof typeof AfriexRewardTier],
  }));

export const getTierStatusOptions = () =>
  Object.keys(AfriexTierStatus).map((key) => ({
    key: key,
    value: AfriexTierStatus[key as keyof typeof AfriexTierStatus],
  }));

export const getLedgerReasonOptions = () => {
  return Object.keys(LedgerReasons).map((key) => {
    const value = LedgerReasons[key as keyof typeof LedgerReasons];
    return {
      key: capitalizeWords(value.replace(/_/g, ' ')),
      value,
    };
  });
};

export const getProcessorsOptions = (processors: string[]) =>
  Object.entries(processors)
    .map(([_, value]) => ({
      key: capitalizeWords(value?.replace(/_/g, ' ')),
      value,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

export const getCorridorPairsOptions = (corridorPairs: string[]) =>
  Object.entries(corridorPairs)
    .map(([_, value]) => ({
      key: value,
      value,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

export const getPaymentInstrumentsOptions = (instruments: string[]) =>
  instruments
    .map((value) => ({
      key: value,
      value,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

export const AfriexBlockReasonsMap = [
  {
    key: 'Sanctions Match',
    value: AfriexBlockReason.SANCTIONS_MATCH,
  },
  {
    key: 'Identity Theft',
    value: AfriexBlockReason.IDENTITY_THEFT,
  },
  {
    key: 'Multiple Disputes',
    value: AfriexBlockReason.MULTIPLE_DISPUTES,
  },
  {
    key: 'Social Engineering',
    value: AfriexBlockReason.SOCIAL_ENGINEERING,
  },
  {
    key: 'Positive Dow Jones match',
    value: AfriexBlockReason.POSITIVE_DOW_JONES_MATCH,
  },
  {
    key: 'Confirmed fraudulent activity',
    value: AfriexBlockReason.CONFIRMED_FRAUDULENT_ACTIVITY,
  },
  {
    key: 'Fraudulent activity investigation ongoing',
    value: AfriexBlockReason.FRAUDULENT_ACTIVITY_INVESTIGATION_ONGOING,
  },
  {
    key: 'Account takeover case',
    value: AfriexBlockReason.ACCOUNT_TAKEOVER_CASE,
  },
  {
    key: 'Unauthorized transactions investigation completed',
    value: AfriexBlockReason.UNAUTHORIZED_TRANSACTIONS_INVESTIGATION_COMPLETED,
  },
  {
    key: 'Identity theft case',
    value: AfriexBlockReason.IDENTITY_THEFT_CASE,
  },
  {
    key: 'ACH fraud',
    value: AfriexBlockReason.ACH_FRAUD,
  },
  {
    key: 'Do not issue virtual account',
    value: AfriexBlockReason.DO_NOT_ISSUE_VIRTUAL_ACCOUNT,
  },
  {
    key: 'Others',
    value: AfriexBlockReason.OTHERS,
  },
];

export const AfriexUnblockReasonsMap = [
  {
    key: 'EDD Provided',
    value: AfriexUnblockReason.EDD_PROVIDED,
  },
  {
    key: 'KYC Place',
    value: AfriexUnblockReason.KYC_IN_PLACE,
  },

  {
    key: 'Watchlist Match Cleared',
    value: AfriexUnblockReason.WATCHLIST_MATCH_CLEARED,
  },
  {
    key: 'Dispute Funds Recovered From Wallet',
    value: AfriexUnblockReason.DISPUTE_FUNDS_RECOVERED_FROM_WALLET,
  },
  {
    key: 'Dispute Resolved',
    value: AfriexUnblockReason.DISPUTE_RESOLVED,
  },
  {
    key: 'Cards reviewed on stripe',
    value: AfriexUnblockReason.CARDS_REVIEWED_ON_STRIPE,
  },
  {
    key: 'Date of birth updated',
    value: AfriexUnblockReason.DATE_OF_BIRTH_UPDATED,
  },
  {
    key: 'Legal names updated',
    value: AfriexUnblockReason.LEGAL_NAMES_UPDATED,
  },
  {
    key: 'Others',
    value: AfriexUnblockReason.OTHERS,
  },
];

export const removeEmptyRows = (arr: AfriexOTCRate[]) =>
  arr?.filter((item) => item.fromSymbol || item.toSymbol || item.value) || [];
